/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState } from 'react';
import { phoneDigits } from 'utils/phone';
import { optionLabel } from '../helpers';
import {
  FREQUENCY_OPTIONS,
  HOSTING_OPTIONS,
  RELATION_OPTIONS,
  RadioOption,
  SALES_TEAM_OPTIONS,
  TEAM_ACCESS_OPTIONS,
} from '../options';
import { ApiRequestForm } from '../types';
import { isCompanyRequesterComplete } from './CompanyRequesterStep';
import { isIpServersComplete } from './IpServersStep';
import { isSalesOperationComplete } from './SalesOperationStep';
import { isTechnicalLegalComplete } from './TechnicalLegalStep';

const NOT_INFORMED = 'Não informado';

interface ReviewStepProps {
  form: ApiRequestForm;
  stepTitles: string[];
  onConsentChange: (accepted: boolean) => void;
  onSend: () => void;
  onBack: () => void;
  submitting: boolean;
  submitError: string;
}

interface ReviewItemProps {
  label: string;
  children: React.ReactNode;
}

const ReviewItem = ({ label, children }: ReviewItemProps) => (
  <div className="mb-3">
    <strong>{label}</strong>
    <div className="text-secondary">{children}</div>
  </div>
);

const orNotInformed = (value: string) => value.trim() || NOT_INFORMED;

const radioLabel = (options: RadioOption<string>[], value: string) => {
  const option = options.find((item) => item.value === value);
  return option ? `${option.label} (${option.description})` : NOT_INFORMED;
};

const listAnswer = (choice: string, rows: React.ReactNode[]) => {
  if (choice === 'nao') return 'Não';
  if (choice === 'sim' && rows.length > 0) return rows;
  return NOT_INFORMED;
};

const ReviewStep: React.FC<ReviewStepProps> = ({
  form,
  stepTitles,
  onConsentChange,
  onSend,
  onBack,
  submitting,
  submitError,
}) => {
  const [submitted, setSubmitted] = useState<boolean>(false);

  const {
    empresa_solicitante: company,
    responsavel_representante: people,
    operacao_sistema: operation,
    ips_servidores: infrastructure,
  } = form;

  const incompleteSteps = [
    isCompanyRequesterComplete(company),
    isTechnicalLegalComplete(people),
    isSalesOperationComplete(operation),
    isIpServersComplete(infrastructure),
  ]
    .map((complete, index) => (complete ? '' : stepTitles[index]))
    .filter(Boolean);

  const consentError =
    submitted && !form.aceite_privacidade
      ? 'Marque esta opção para enviar a solicitação.'
      : undefined;

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);

    if (incompleteSteps.length === 0 && form.aceite_privacidade) onSend();
  };

  return (
    <form noValidate onSubmit={handleSubmit}>
      <p className="card-text font-weight-bold mt-4">Confirme os dados antes de enviar</p>

      {incompleteSteps.length > 0 && (
        <div className="alert alert-warning" role="alert">
          Falta completar: {incompleteSteps.join(', ')}. Volte a essa etapa para corrigir.
        </div>
      )}

      <div className="row mb-3">
        <div className="col-md-6">
          <div className="border p-3 rounded h-100">
            <p className="font-weight-bold border-bottom pb-2 mb-3">1. {stepTitles[0]}</p>
            <ReviewItem label="Razão social">{orNotInformed(company.razao_social)}</ReviewItem>
            <ReviewItem label="CNPJ">{orNotInformed(company.cnpj_contratante)}</ReviewItem>
            <ReviewItem label="Outros CNPJs que usam o ConLicitação">
              {listAnswer(
                company.usa_outros_cnpjs,
                company.outros_cnpjs.map((row) => (
                  <div key={row.id}>{orNotInformed(row.cnpj)}</div>
                )),
              )}
            </ReviewItem>
            <ReviewItem label="Nome do solicitante">
              {orNotInformed(company.nome_solicitante)}
            </ReviewItem>
            <ReviewItem label="E-mail corporativo">
              {orNotInformed(company.email_corporativo)}
            </ReviewItem>
            <ReviewItem label="Celular">{orNotInformed(company.telefone_celular)}</ReviewItem>
            <ReviewItem label="Telefone fixo">
              {phoneDigits(company.telefone_fixo) ? company.telefone_fixo : NOT_INFORMED}
            </ReviewItem>

            <p className="font-weight-bold border-bottom pb-2 mb-3 mt-4">2. {stepTitles[1]}</p>
            <ReviewItem label="Nome do responsável técnico">
              {orNotInformed(people.tecnico_nome)}
            </ReviewItem>
            <ReviewItem label="E-mail do responsável técnico">
              {orNotInformed(people.tecnico_email)}
            </ReviewItem>
            <ReviewItem label="Celular do responsável técnico">
              {orNotInformed(people.tecnico_telefone)}
            </ReviewItem>
            <ReviewItem label="Vínculo com a empresa">
              {optionLabel(RELATION_OPTIONS, people.tecnico_vinculo) || NOT_INFORMED}
            </ReviewItem>
            <ReviewItem label="Nome do representante legal">
              {orNotInformed(people.rep_legal_nome)}
            </ReviewItem>
            <ReviewItem label="CPF do representante legal">
              {orNotInformed(people.rep_legal_cpf)}
            </ReviewItem>
            <ReviewItem label="Outras empresas do representante legal">
              {listAnswer(
                people.rep_legal_tem_outras_empresas,
                people.rep_legal_outras_empresas.map((row) => (
                  <div key={row.id}>
                    {orNotInformed(row.nome)} · CNPJ {orNotInformed(row.cnpj)}
                  </div>
                )),
              )}
            </ReviewItem>
          </div>
        </div>

        <div className="col-md-6 mt-3 mt-md-0">
          <div className="border p-3 rounded h-100">
            <p className="font-weight-bold border-bottom pb-2 mb-3">3. {stepTitles[2]}</p>
            <ReviewItem label="Equipe de vendas ao governo">
              {radioLabel(SALES_TEAM_OPTIONS, operation.equipe_vendas)}
            </ReviewItem>
            <ReviewItem label="Acesso da equipe às licitações">
              {radioLabel(TEAM_ACCESS_OPTIONS, operation.acesso_equipe)}
            </ReviewItem>
            <ReviewItem label="Pessoas no setor de licitação">
              {orNotInformed(operation.qtd_pessoas)}
            </ReviewItem>
            <ReviewItem label="Como as licitações serão usadas">
              <div className="pre-line">{orNotInformed(operation.uso_licitacoes)}</div>
            </ReviewItem>
            <ReviewItem label="Sistema de destino">
              {orNotInformed(operation.sistema_destino)}
            </ReviewItem>
            <ReviewItem label="Aplicação que vai receber as licitações">
              {orNotInformed(operation.motor_busca_nome)}
            </ReviewItem>
            <ReviewItem label="Frequência de recebimento">
              {optionLabel(FREQUENCY_OPTIONS, operation.frequencia) || NOT_INFORMED}
            </ReviewItem>

            <p className="font-weight-bold border-bottom pb-2 mb-3 mt-4">4. {stepTitles[3]}</p>
            {infrastructure.ips.map((row, index) => (
              <ReviewItem key={row.id} label={`IP ${index + 1}`}>
                <div>
                  {orNotInformed(row.ip)}{' '}
                  <span className="text-muted">({row.ip_versao === 'ipv6' ? 'IPv6' : 'IPv4'})</span>
                </div>
                <div>{optionLabel(HOSTING_OPTIONS, row.tipo_hospedagem) || NOT_INFORMED}</div>
                {row.tipo_hospedagem === 'operadora' && (
                  <>
                    <div>CNPJ titular: {orNotInformed(row.ip_titular_cnpj)}</div>
                    <div>Cidade: {orNotInformed(row.ip_localizacao)}</div>
                  </>
                )}
                {row.tipo_hospedagem === 'nuvem' && (
                  <>
                    <div>Provedor: {orNotInformed(row.provedor_nuvem)}</div>
                    <div>Região: {orNotInformed(row.ip_localizacao)}</div>
                  </>
                )}
              </ReviewItem>
            ))}
            <ReviewItem label="Cidade ou região dos servidores">
              {orNotInformed(infrastructure.servidores_localizacao)}
            </ReviewItem>
          </div>
        </div>
      </div>

      <div className="form-check mb-4">
        <input
          id="api_request_aceite_privacidade"
          type="checkbox"
          className={`form-check-input ${consentError ? 'is-invalid' : ''}`}
          checked={form.aceite_privacidade}
          onChange={(e) => onConsentChange(e.target.checked)}
        />
        <label className="form-check-label" htmlFor="api_request_aceite_privacidade">
          Declaro que as informações são verdadeiras e autorizo o uso delas na análise e na
          liberação do acesso à API.
        </label>
        {consentError && <div className="invalid-feedback">{consentError}</div>}
      </div>

      {submitError && (
        <div className="alert alert-danger text-center py-2" role="alert">
          {submitError}
        </div>
      )}

      <div className="row mt-2">
        <div className="col-md-12">
          <button type="submit" className="btn btn-lg btn-block btn-submit" disabled={submitting}>
            {submitting ? 'Enviando…' : 'Enviar solicitação'}
          </button>
          <div className="text-center mt-2">
            <button type="button" className="btn btn-link" onClick={onBack} disabled={submitting}>
              Voltar
            </button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ReviewStep;
